import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { TouchableInputProps } from '.';
import { STATE_CODE_TO_LABEL } from '../../../constants';
import { analyticsTrackEvent } from '../../../core/analytics';
import { ACTION_CHANGE_VALUE, UNIVERSAL_STATE_INPUT } from '../../../core/analytics/events';
import { apiGetJson } from '../../../core/dapi';
import { getTelemedStatesAvailableUrl } from '../../../core/dapi/location';
import { sortAlphabetical } from '../../../core/util/array';
import { AutoCompleteResult } from '../AutocompleteResults';
import { FieldDisplayProps } from './inputs/Field';
import TextInput from './inputs/TextInput';

type Props = { onChange: (state: string) => void } & TouchableInputProps &
  Omit<FieldDisplayProps, 'label'>;

/**
 * Input to select a state for a video visit
 */
const StateInput = ({ onChange, touched, ...props }: Props) => {
  const [stateSelected, setStateSelected] = useState('');
  const [states, setStates] = useState<string[]>([]);

  useEffect(() => {
    apiGetJson(getTelemedStatesAvailableUrl()).then((json: any) => {
      const response = json?.data;
      if (!isEmpty(response)) {
        setStates(response);
      }
    });
  }, []);

  const options = useMemo(() => {
    const entries = states
      .map((state) => {
        const lowerState = state.toLowerCase() as keyof typeof STATE_CODE_TO_LABEL;
        const label = STATE_CODE_TO_LABEL[lowerState];
        return {
          key: lowerState,
          primary: label,
          value: lowerState,
          onClick: () => {
            setStateSelected(lowerState.toUpperCase());
            onChange(lowerState);
            analyticsTrackEvent(UNIVERSAL_STATE_INPUT, {
              action: ACTION_CHANGE_VALUE,
              state: lowerState.toUpperCase(),
            });
          },
        } as AutoCompleteResult;
      })
      // Filter by those that match the current input
      .filter((state) => {
        return (
          state.key.toLowerCase().startsWith(stateSelected.toLowerCase()) ||
          state.primary.toLowerCase().startsWith(stateSelected.toLowerCase())
        );
      });
    sortAlphabetical(entries, (entry: any) => entry.primary);

    return entries;
  }, [onChange, stateSelected, states]);

  return (
    <TextInput
      hideDivider
      label="State"
      onChange={setStateSelected}
      placeholder="Where are you?"
      results={options.slice(0, 7)}
      selectAllOnFocus
      setIsTouched={touched}
      trackingLabel={UNIVERSAL_STATE_INPUT}
      value={stateSelected}
      {...props}
    />
  );
};

export default StateInput;
